/**
 * This billing month's bill split by OpCo (docs/BILLING_DESIGN.md).
 *
 * Microsoft bills the organisation; this split is for the organisation's own
 * records. Each OpCo carries its share of the cost so far (monthly fee plus
 * extra pages) in proportion to its pages, and the shares add up to the bill
 * to the penny. An OpCo admin in the All-OpCos view sees every OpCo; a manager
 * in their workspace sees their own row against the organisation's total.
 * A month picker offers past billing months; a closed month is frozen.
 */
import { Building2 } from 'lucide-react'
import { Card, HelpTip } from './BillingPrimitives'
import { formatBillingMonth, formatInt, formatMoneyAs } from '../../utils/billing'

export default function OpCoBillSplit({
  bill, ownOnly = false, displayCurrency,
  cycles = [], selectedStart = null, onSelectCycle, loading = false, error = null,
}) {
  if (!bill && !loading && !error) return null
  const closed = Boolean(selectedStart)
  const showPicker = cycles.length > 1 && typeof onSelectCycle === 'function'
  const openStart = cycles[0]?.start
  const picker = showPicker && (
    <select
      aria-label="Billing month"
      value={selectedStart || openStart}
      onChange={(e) => onSelectCycle(e.target.value === openStart ? null : e.target.value)}
      className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-700 dark:text-gray-200"
    >
      {cycles.map((c, i) => (
        <option key={c.start} value={c.start}>
          {formatBillingMonth(c)}{i === 0 ? ' (current)' : ''}
        </option>
      ))}
    </select>
  )
  if (!bill) {
    return (
      <Card className="p-5" data-testid="opco-bill-split">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Bill by OpCo</h3>
          {picker}
        </div>
        <p role={error ? 'alert' : undefined} className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {error || 'Loading…'}
        </p>
      </Card>
    )
  }
  const priced = bill.org_cost !== null && bill.org_cost !== undefined
  const money = (v) => formatMoneyAs(v, bill.currency, displayCurrency)
  const rows = bill.opcos || []

  return (
    <Card className="p-5" data-testid="opco-bill-split">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {closed
              ? (ownOnly ? "Your OpCo's share of the bill" : 'Bill by OpCo')
              : (ownOnly ? "Your OpCo's share of this month's bill" : "This month's bill by OpCo")}
          </h3>
          <HelpTip>
            Approximate: the organisation's bill split in proportion to pages processed. Microsoft
            bills the organisation as a whole; this split is for your own records. A request moved
            to another OpCo takes its pages with it for the current billing month only, so a
            closed month never changes.
          </HelpTip>
        </div>
        <div className="flex items-center gap-2">
          {picker || (
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {formatBillingMonth(bill.cycle)}
            </span>
          )}
        </div>
      </div>
      {closed && (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400" data-testid="opco-bill-frozen">
          Closed month: these figures are frozen and don't change when requests are moved later.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <th scope="col" className="py-2 pr-3 font-medium">OpCo</th>
              <th scope="col" className="py-2 px-3 font-medium text-right">Pages</th>
              <th scope="col" className="py-2 px-3 font-medium text-right">Share of pages</th>
              {priced && <th scope="col" className="py-2 pl-3 font-medium text-right">Share of bill</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.opco_id ?? 'unassigned'} className="border-b border-gray-50 dark:border-gray-800/60">
                <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">
                  {r.name}
                  {r.code && <span className="ml-2 text-xs text-gray-400 uppercase">{r.code}</span>}
                </td>
                <td className="py-2 px-3 text-right tabular-nums">{formatInt(r.pages)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.percent_of_pages}%</td>
                {priced && <td className="py-2 pl-3 text-right tabular-nums font-medium">{money(r.cost_share)}</td>}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-gray-700 dark:text-gray-300">
              <td className="pt-2 pr-3 font-medium">Organisation</td>
              <td className="pt-2 px-3 text-right tabular-nums">{formatInt(bill.org_pages)}</td>
              <td className="pt-2 px-3 text-right tabular-nums">{bill.org_pages ? '100%' : '–'}</td>
              {priced && <td className="pt-2 pl-3 text-right tabular-nums font-semibold">{money(bill.org_cost)}</td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {!priced && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          No plan is set up yet, so pages are shown without a price.
        </p>
      )}
      {priced && bill.unallocated_cost > 0 && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Nothing has been processed this billing month yet, so the {money(bill.unallocated_cost)} monthly
          fee isn't split until pages are processed.
        </p>
      )}
    </Card>
  )
}
